import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Shield,
  Smartphone,
  Mail,
  MessageSquare,
  Key,
  ArrowLeft,
  Loader,
  Send,
  Clock,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';
import { useForceLightMode } from '../hooks/useForceLightMode';
import type { MfaMethod } from '../types';

type MethodTab = MfaMethod | 'BACKUP';

const METHOD_META: Record<MethodTab, { icon: React.ElementType; label: string }> = {
  TOTP: { icon: Smartphone, label: 'Authenticator' },
  EMAIL: { icon: Mail, label: 'Email' },
  SMS: { icon: MessageSquare, label: 'SMS' },
  BACKUP: { icon: Key, label: 'Backup Code' },
};

export const MfaVerifyPage: React.FC = () => {
  useForceLightMode();
  const navigate = useNavigate();
  const { mfaChallenge, verifyMfa, sendMfaOtp, clearMfaChallenge, isLoading } = useAuth();

  const [method, setMethod] = useState<MethodTab>(mfaChallenge?.preferredMethod ?? 'TOTP');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Redirect if no challenge
  useEffect(() => {
    if (!mfaChallenge) {
      navigate('/signin', { replace: true });
    } else {
      setSecondsLeft(mfaChallenge.expiresIn);
    }
  }, [mfaChallenge, navigate]);

  // Countdown timer
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [secondsLeft]);

  // OTP send cooldown
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const interval = setInterval(() => {
      setOtpCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [otpCooldown]);

  // Focus input on method change
  useEffect(() => {
    setCode('');
    setError('');
    setOtpSent(false);
    inputRef.current?.focus();
  }, [method]);

  if (!mfaChallenge) return null;

  const availableTabs: MethodTab[] = [
    ...mfaChallenge.availableMethods,
    ...(mfaChallenge.backupCodesAvailable ? (['BACKUP'] as MethodTab[]) : []),
  ];

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const handleSendOtp = async () => {
    if (method !== 'EMAIL' && method !== 'SMS') return;
    try {
      await sendMfaOtp(method);
      setOtpSent(true);
      setOtpCooldown(60);
    } catch (err: any) {
      setError(err?.message || 'Failed to send code');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setError('');

    try {
      await verifyMfa(code.trim(), method, trustDevice);
      sessionStorage.setItem('isReturningUser', 'true');
      navigate('/home');
    } catch (err: any) {
      setError(err?.message || 'Verification failed. Please try again.');
    }
  };

  const handleCancel = () => {
    clearMfaChallenge();
    navigate('/signin', { replace: true });
  };

  const needsOtpSend = (method === 'EMAIL' || method === 'SMS') && !otpSent;

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <button
            onClick={handleCancel}
            className="inline-flex items-center space-x-2 text-medical-600 hover:text-medical-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Sign In</span>
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-medical-200 p-8">
          <div className="mb-6 text-center">
            <Logo size="lg" className="justify-center" />
          </div>

          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full mb-3">
              <Shield className="h-6 w-6 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-medical-900 mb-1">Two-Factor Authentication</h1>
            <p className="text-medical-600 text-sm">
              Enter your verification code to continue
            </p>
          </div>

          {/* Timer */}
          {secondsLeft > 0 && !needsOtpSend && (
            <div className="flex items-center justify-center space-x-1 text-xs text-medical-500 mb-4">
              <Clock className="h-3 w-3" />
              <span>Expires in {formatTime(secondsLeft)}</span>
            </div>
          )}
          {secondsLeft <= 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 text-center">
              <p className="text-sm text-red-600">Challenge expired. Please sign in again.</p>
            </div>
          )}

          {/* Method tabs */}
          {availableTabs.length > 1 && (
            <div className="flex space-x-1 mb-5 bg-medical-100 rounded-lg p-1">
              {availableTabs.map((tab) => {
                const { icon: Icon, label } = METHOD_META[tab];
                return (
                  <button
                    key={tab}
                    onClick={() => setMethod(tab)}
                    className={`flex-1 flex items-center justify-center space-x-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                      method === tab
                        ? 'bg-white text-primary-700 shadow-sm'
                        : 'text-medical-600 hover:text-medical-800'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Send OTP button for email/SMS */}
            {needsOtpSend && (
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={isLoading}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                <Send className="h-4 w-4" />
                <span>Send Code via {method === 'EMAIL' ? 'Email' : 'SMS'}</span>
              </button>
            )}

            {/* Code input (always visible for TOTP/BACKUP, after send for EMAIL/SMS) */}
            {!needsOtpSend && (
              <>
                <div>
                  <label className="block text-sm font-medium text-medical-700 mb-2">
                    {method === 'TOTP'
                      ? 'Enter code from your authenticator app'
                      : method === 'BACKUP'
                        ? 'Enter a backup code (XXXX-XXXX)'
                        : `Enter the code sent to your ${method === 'EMAIL' ? 'email' : 'phone'}`}
                  </label>
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode={method === 'BACKUP' ? 'text' : 'numeric'}
                    autoComplete="one-time-code"
                    maxLength={method === 'BACKUP' ? 9 : 6}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="input-field text-center text-2xl tracking-[0.3em] font-mono"
                    placeholder={method === 'BACKUP' ? 'XXXX-XXXX' : '000000'}
                    autoFocus
                  />
                </div>

                {/* Resend for email/SMS */}
                {(method === 'EMAIL' || method === 'SMS') && otpSent && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={otpCooldown > 0}
                      className="text-sm text-primary-600 hover:text-primary-700 disabled:text-medical-400"
                    >
                      {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Resend code'}
                    </button>
                  </div>
                )}

                {method === 'EMAIL' && otpSent && (
                  <p className="text-xs text-medical-500 text-center">
                    Can't find the email? Check your spam or junk folder.
                  </p>
                )}

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    className="rounded border-medical-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-medical-600">Trust this device for 30 days</span>
                </label>

                <button
                  type="submit"
                  disabled={isLoading || !code.trim() || secondsLeft <= 0}
                  className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <span>Verify</span>
                  )}
                </button>
              </>
            )}
          </form>

          <div className="mt-6 flex items-center justify-center space-x-2 text-xs text-medical-500">
            <Shield className="h-4 w-4" />
            <span>256-bit SSL encryption</span>
          </div>
        </div>
      </div>
    </div>
  );
};
